import axios from 'axios';
import React, { useEffect, useState } from 'react';
import apiClient from '../../../utils/apiclient';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../utils/firebaseConfig';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { MapContainer, TileLayer, Marker, CircleMarker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet's default icon issue with React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);
  return null;
};

// Helper component to force Leaflet to recalculate map size when fullscreen toggles
const FullscreenHandler = ({ isFullscreen }) => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300); // 300ms delay to allow DOM/CSS transitions to finish
    return () => clearTimeout(timer);
  }, [isFullscreen, map]);
  return null;
};

// Helper component to manage map interactions (dragging, pinch-zoom, etc)
const MapInteractionController = ({ isFullscreen }) => {
  const map = useMap();
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const shouldDisable = isMobile && !isFullscreen;

    if (shouldDisable) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      if (map.tap) map.tap.disable();
    } else {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();
    }
  }, [isFullscreen, map]);

  return null;
};

const RiderRow = ({ rider, index }) => {
  const navigate = useNavigate();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [liveLocation, setLiveLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [fcmTokenExists, setFcmTokenExists] = useState(false);
  const [address, setAddress] = useState("Loading address...");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Initial check and setup real-time listener if map is open or tracking is checked
  useEffect(() => {
    if (!rider.id) return;

    // Listen to the Firestore document for this rider
    const unsubscribe = onSnapshot(doc(db, "Riders", rider.id.toString()), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.adminTrackingRequested !== undefined) {
          setIsTracking(data.adminTrackingRequested);
        }
        if (data.fcmToken) {
          setFcmTokenExists(true);
        } else {
          setFcmTokenExists(false);
        }
        if (data.latitude && data.longitude) {
          const newLoc = {
            lat: data.latitude,
            lng: data.longitude,
            updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : Date.now()
          };
          setLiveLocation(newLoc);

          // Append to history if it's a new unique coordinate
          setLocationHistory(prev => {
            if (prev.length === 0) return [[newLoc.lat, newLoc.lng]];
            const last = prev[prev.length - 1];
            if (last[0] !== newLoc.lat || last[1] !== newLoc.lng) {
              // Calculate Haversine distance in kilometers
              const p = 0.017453292519943295;    // Math.PI / 180
              const c = Math.cos;
              const a = 0.5 - c((newLoc.lat - last[0]) * p)/2 + 
                      c(last[0] * p) * c(newLoc.lat * p) * 
                      (1 - c((newLoc.lng - last[1]) * p))/2;
              const distanceKm = 12742 * Math.asin(Math.sqrt(a)); // 2 * R; R = 6371 km

              // If the jump is greater than 5 kilometers at once, break the line
              // This prevents massive lines from being drawn if the user logs in on 2 devices in different cities
              if (distanceKm > 5) {
                return [[newLoc.lat, newLoc.lng]];
              }

              return [...prev, [newLoc.lat, newLoc.lng]];
            }
            return prev;
          });
        }
      } else {
        setFcmTokenExists(false);
      }
    });

    return () => unsubscribe();
  }, [rider.id]);

  const toggleTracking = async () => {
    if (!fcmTokenExists && !isTracking) {
      alert(`Cannot activate tracking: ${rider.name} hasn't logged into the app yet, so their device cannot be reached.`);
      return;
    }

    const newValue = !isTracking;
    setIsTracking(newValue); // Optimistic UI update

    try {
      const riderRef = doc(db, "Riders", rider.id.toString());
      await setDoc(riderRef, {
        adminTrackingRequested: newValue
      }, { merge: true });
    } catch (error) {
      console.error("Error updating tracking request:", error);
      setIsTracking(!newValue); // Revert on failure
      alert("Error updating tracking. Please ensure Firebase configuration is set up correctly in src/utils/firebaseConfig.js");
    }
  };

  const currentLat = liveLocation?.lat || rider.latitude || 0;
  const currentLng = liveLocation?.lng || rider.longitude || 0;

  // Reverse Geocoding to get human-readable address
  useEffect(() => {
    if (currentLat !== 0 && currentLng !== 0 && isMapOpen) {
      setAddress("Loading address...");
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${currentLat}&lon=${currentLng}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.display_name) {
            // Shorten the address slightly to fit better
            const parts = data.display_name.split(', ');
            setAddress(parts.slice(0, 3).join(', '));
          } else {
            setAddress("Address not found");
          }
        })
        .catch(() => setAddress("Error loading address"));
    }
  }, [currentLat, currentLng, isMapOpen]);

  return (
    <>
      <tr className='items-center justify-center border border-gray-300'>
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{rider.id}</td>
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{rider.name}</td>
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center hover:cursor-pointer hover:underline text-blue-600" onClick={() => { navigate(`/userDetails/${rider.number}`) }}>{rider.number}</td>
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center font-medium text-gray-500">
          {liveLocation?.updatedAt ? new Date(liveLocation.updatedAt).toLocaleTimeString() : 'Unknown'}
        </td>

        {/* Tracking Toggle */}
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
          <button
            onClick={toggleTracking}
            className={`px-2 py-0.5 md:px-4 md:py-1 text-[10px] md:text-sm rounded-full text-white font-semibold transition-colors ${isTracking ? 'bg-green-500' : 'bg-gray-400'}`}
          >
            {isTracking ? 'Active' : 'Idle'}
          </button>
        </td>

        {/* Live Map Button */}
        <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
          <button
            onClick={() => setIsMapOpen(!isMapOpen)}
            className="px-2 py-0.5 md:px-3 md:py-1 text-[10px] md:text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            {isMapOpen ? 'Hide Map' : 'View Map'}
          </button>
        </td>
      </tr>

      {/* Expanded Map Row */}
      {isMapOpen && (
        <tr>
          <td colSpan="6" className="p-0 md:p-4 bg-gray-50 border border-gray-300">
            <div className={
              isFullscreen 
                ? "fixed top-[5vh] left-[5vw] z-[999999] w-[90vw] h-[90vh] bg-white rounded-xl shadow-2xl ring-[10000px] ring-black/50 overflow-hidden" 
                : "w-[85vw] md:w-full h-96 rounded shadow-inner border border-gray-200 relative z-0 sticky left-2 md:relative"
            }>
              {(currentLat === 0 && currentLng === 0) ? (
                <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">
                  No location data available for this rider.
                </div>
              ) : (
                <>
                  <div className="absolute top-2 right-2 md:top-4 md:right-4 z-[9999] bg-white/90 md:bg-white/95 backdrop-blur-md px-2 py-1.5 md:px-4 md:py-3 rounded-md md:rounded-xl shadow-md md:shadow-xl border border-gray-200/50 flex flex-col gap-0.5 md:gap-1.5 text-right max-w-[140px] md:max-w-xs pointer-events-auto">
                    {isTracking ? (
                      <span className="text-green-600 font-bold text-[10px] md:text-sm flex items-center justify-end gap-1.5 md:gap-2">
                        <span className="relative flex h-2 w-2 md:h-2.5 md:w-2.5 shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 md:h-2.5 md:w-2.5 bg-green-500"></span>
                        </span>
                        Live Updates Active
                      </span>
                    ) : (
                      <span className="text-gray-500 font-bold text-[10px] md:text-sm flex items-center justify-end gap-1.5 md:gap-2">
                        <span className="h-2 w-2 md:h-2.5 md:w-2.5 rounded-full bg-gray-400 shrink-0"></span>
                        Tracking Paused
                      </span>
                    )}
                    <span className="text-[9px] md:text-xs text-gray-600 font-medium break-words md:mt-1">
                      📍 {address}
                    </span>
                    <span className="text-[8px] md:text-[10px] text-gray-400 font-normal uppercase tracking-wide">
                      Updated: {liveLocation?.updatedAt ? new Date(liveLocation.updatedAt).toLocaleTimeString() : 'Unknown'}
                    </span>
                  </div>

                  {/* Fullscreen Toggle Button */}
                  <div className="absolute bottom-4 left-4 z-[9999] pointer-events-auto">
                    <button
                      onClick={() => setIsFullscreen(!isFullscreen)}
                      className="bg-white/90 backdrop-blur-md px-3 py-2 rounded-lg shadow-lg border border-gray-200/50 text-xs md:text-sm font-semibold text-gray-800 hover:bg-white transition-colors flex items-center gap-2"
                    >
                      {isFullscreen ? (
                        <>
                          <span className="text-lg leading-none">↙</span> Exit Fullscreen
                        </>
                      ) : (
                        <>
                          <span className="text-lg leading-none">↗</span> Fullscreen
                        </>
                      )}
                    </button>
                  </div>
                  <MapContainer center={[currentLat, currentLng]} zoom={16} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                      url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                      attribution='&copy; Google Maps'
                    />
                    {locationHistory.length > 1 && (
                      <>
                        <Polyline positions={locationHistory} color="#1e40af" weight={8} opacity={0.8} />
                        <Polyline positions={locationHistory} color="#3b82f6" weight={5} opacity={1} />
                      </>
                    )}
                    
                    {/* Outer halo */}
                    <CircleMarker 
                      center={[currentLat, currentLng]} 
                      radius={24}
                      pathOptions={{ color: 'transparent', fillColor: '#3b82f6', fillOpacity: 0.2 }}
                      pane="markerPane"
                    />
                    {/* White border and inner blue dot */}
                    <CircleMarker 
                      center={[currentLat, currentLng]} 
                      radius={8}
                      pathOptions={{ color: '#ffffff', fillColor: '#2563eb', fillOpacity: 1, weight: 3 }}
                      pane="markerPane"
                    >
                      <Popup>
                        {rider.name}<br />
                        Updated: {liveLocation?.updatedAt ? new Date(liveLocation.updatedAt).toLocaleTimeString() : 'Unknown'}
                      </Popup>
                    </CircleMarker>
                    <MapUpdater center={[currentLat, currentLng]} />
                    <FullscreenHandler isFullscreen={isFullscreen} />
                    <MapInteractionController isFullscreen={isFullscreen} />
                  </MapContainer>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const AllRiders = (route) => {
  const [data, setData] = useState([]);
  const [search, Setsearch] = useState("");
  const [isloading, SetIsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers(search);
  }, [route.route, page, search]);

  const fetchUsers = async (number = "") => {
    try {
      SetIsLoading(true);

      const response = await apiClient.get(
        `${route.route}?page=${page}&size=${size}&search=${number}`
      );

      setData(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      SetIsLoading(false);
    }
  };

  const filteredData = Array.isArray(data)
    ? data.filter((el) => {
      if (search.trim() === "") return true;

      return (
        el.name?.toLowerCase().includes(search.toLowerCase()) ||
        el.number?.toString().includes(search) ||
        el.email?.toLowerCase().includes(search.toLowerCase()) ||
        el.id?.toString().includes(search)
      );
    })
    : [];

  const handlechange = (e) => {
    const value = e.target.value;
    Setsearch(value);
  };

  return (
    <div className="w-full flex-1 flex flex-col min-h-0">
      <div className="flex justify-between items-center mb-2 flex-shrink-0 gap-3">
        <h2 className="text-base md:text-xl font-semibold text-gray-800 whitespace-nowrap">Riders Live Tracking</h2>
        <input className="flex-1 border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500 min-w-0"
          placeholder='Search rider name, email, or ID...'
          onChange={handlechange}
          value={search}
        />
      </div>

      <div className="w-full flex-1 overflow-auto pb-4 relative">
        {isloading && (
          <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-30">
            <div className="text-lg font-semibold text-blue-600 animate-pulse">
              Loading Riders...
            </div>
          </div>
        )}

        <table className='min-w-max w-full min-w-max bg-white shadow-md border-collapse'>
          <thead className="bg-gray-800 text-white sticky top-0 z-20 shadow-md">
            <tr>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">ID</th>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">Name</th>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">Phone</th>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">Last Updated Location</th>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">Tracking Control</th>
              <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm font-semibold tracking-wide text-center">Live Map</th>
            </tr>
          </thead>
          <tbody>
            {isloading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">Loading...</td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500">No riders found.</td>
              </tr>
            ) : (
              filteredData.map((rider, index) => (
                <RiderRow key={rider.id || index} rider={rider} index={index} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center items-center gap-4 mt-4 pb-4 flex-shrink-0">
        <button
          disabled={page === 0 || isloading}
          onClick={() => setPage((prev) => prev - 1)}
          className="px-6 py-2 bg-gray-800 text-white font-semibold rounded disabled:opacity-50 hover:bg-gray-700 transition-colors"
        >
          Previous
        </button>

        <span className="font-medium text-gray-700">
          Page {page + 1} of {totalPages}
        </span>

        <button
          disabled={page >= totalPages - 1 || isloading}
          onClick={() => setPage((prev) => prev + 1)}
          className="px-6 py-2 bg-gray-800 text-white font-semibold rounded disabled:opacity-50 hover:bg-gray-700 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AllRiders;